import { useState } from 'react';
import { Icon } from './Icon.jsx';
import { SignatureModal } from './SignatureModal.jsx';
import { useAccount } from '../store/accountStore.js';
import { useT } from '../i18n/index.js';
import { useToast } from './Toast.jsx';

// CRUD section for label + (optional multiline) text entries.
function TextSection({ kind, title, hint, multiline, placeholder }) {
  const t = useT();
  const items = useAccount((s) => s[kind]);
  const defaults = useAccount((s) => s.defaults);
  const addItem = useAccount((s) => s.addItem);
  const updateItem = useAccount((s) => s.updateItem);
  const deleteItem = useAccount((s) => s.deleteItem);
  const setDefault = useAccount((s) => s.setDefault);
  const moveItem = useAccount((s) => s.moveItem);

  const [editId, setEditId] = useState(null);
  const [label, setLabel] = useState('');
  const [text, setText] = useState('');

  const reset = () => {
    setEditId(null);
    setLabel('');
    setText('');
  };
  const submit = () => {
    const value = multiline ? text.trim() : label.trim();
    if (!value) return;
    const lbl = label.trim() || value.split('\n')[0];
    const payload = multiline ? { label: lbl, text: text.trim() } : { value: label.trim() };
    if (editId) updateItem(kind, editId, payload);
    else addItem(kind, payload);
    reset();
  };
  const edit = (it) => {
    setEditId(it.id);
    setLabel(it.label || it.value || '');
    setText(it.text || '');
  };

  return (
    <section className="acc-section">
      <div className="acc-section-head">
        <h2>{title}</h2>
        {hint && <span className="tiny muted">{hint}</span>}
      </div>

      <div className="acc-form">
        <input
          className="letter-input"
          placeholder={multiline ? t('account.label_ph') : placeholder}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        {multiline && (
          <textarea
            className="letter-input"
            rows={3}
            placeholder={placeholder}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        )}
        <div className="row" style={{ gap: 8 }}>
          <button className="btn primary sm" onClick={submit}>
            <Icon name={editId ? 'check' : 'plus'} size={14} />
            {editId ? t('account.save') : t('account.add')}
          </button>
          {editId && (
            <button className="btn sm ghost" onClick={reset}>{t('modal.cancel')}</button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="tiny muted">{t('account.empty')}</div>
      ) : (
        <div className="acc-list">
          {items.map((it, i) => (
            <div key={it.id} className={`acc-item ${defaults[kind] === it.id ? 'default' : ''}`}>
              <button
                className="acc-default-btn"
                onClick={() => setDefault(kind, it.id)}
                title={t('account.set_default')}
                aria-label={t('account.set_default')}
              >
                <span className={`acc-radio ${defaults[kind] === it.id ? 'on' : ''}`} />
              </button>
              <div className="acc-item-body">
                <div className="acc-item-label">{it.label || it.value}</div>
                {multiline && it.text && (
                  <div className="acc-item-text tiny muted">{it.text.split('\n')[0]}…</div>
                )}
              </div>
              <button className="btn icon sm" onClick={() => moveItem(kind, it.id, 'up')} disabled={i === 0} aria-label={t('account.move_up')} title={t('account.move_up')}>
                <Icon name="chevLeft" size={13} style={{ transform: 'rotate(90deg)' }} />
              </button>
              <button className="btn icon sm" onClick={() => moveItem(kind, it.id, 'down')} disabled={i === items.length - 1} aria-label={t('account.move_down')} title={t('account.move_down')}>
                <Icon name="chevLeft" size={13} style={{ transform: 'rotate(-90deg)' }} />
              </button>
              <button className="btn icon sm" onClick={() => edit(it)} aria-label={t('account.edit')} title={t('account.edit')}>
                <Icon name="text" size={13} />
              </button>
              <button className="btn icon sm danger" onClick={() => deleteItem(kind, it.id)} aria-label={t('account.delete')} title={t('account.delete')}>
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function SignatureSection() {
  const t = useT();
  const items = useAccount((s) => s.signatures);
  const defaults = useAccount((s) => s.defaults);
  const addItem = useAccount((s) => s.addItem);
  const deleteItem = useAccount((s) => s.deleteItem);
  const setDefault = useAccount((s) => s.setDefault);
  const push = useToast((s) => s.push);
  const [open, setOpen] = useState(false);

  return (
    <section className="acc-section">
      <div className="acc-section-head">
        <h2>{t('account.signatures')}</h2>
        <span className="tiny muted">{t('account.signatures_hint')}</span>
      </div>

      <button className="btn primary sm" onClick={() => setOpen(true)}>
        <Icon name="sign" size={14} /> {t('account.add_signature')}
      </button>

      {items.length === 0 ? (
        <div className="tiny muted">{t('account.empty')}</div>
      ) : (
        <div className="acc-sig-list">
          {items.map((it) => (
            <div key={it.id} className={`acc-sig ${defaults.signatures === it.id ? 'default' : ''}`}>
              <button
                className="acc-default-btn"
                onClick={() => setDefault('signatures', it.id)}
                title={t('account.set_default')}
                aria-label={t('account.set_default')}
              >
                <span className={`acc-radio ${defaults.signatures === it.id ? 'on' : ''}`} />
              </button>
              <img src={it.dataUrl} alt={it.label} className="acc-sig-img" />
              <span className="acc-item-label">{it.label}</span>
              <button
                className="btn icon sm danger"
                onClick={() => deleteItem('signatures', it.id)}
                aria-label={t('account.delete')}
                title={t('account.delete')}
              >
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      <SignatureModal
        open={open}
        onClose={() => setOpen(false)}
        onSave={(sig) => {
          const label = window.prompt(t('account.signature_name'), 'Signature');
          addItem('signatures', {
            label: (label || 'Signature').trim(),
            dataUrl: sig.dataUrl,
            w: sig.w,
            h: sig.h,
          });
          setOpen(false);
          push({ message: t('account.toast_saved'), kind: 'success' });
        }}
      />
    </section>
  );
}

export function AccountView() {
  const t = useT();
  return (
    <div className="account-view">
      <div className="account-head">
        <h1>{t('account.title')}</h1>
        <span className="sub">{t('account.subtitle')}</span>
      </div>
      <div className="account-grid">
        <TextSection
          kind="senders"
          title={t('account.senders')}
          hint={t('account.senders_hint')}
          multiline
          placeholder={'Prénom Nom\nAdresse\n75000 Ville\nemail@exemple.fr'}
        />
        <TextSection
          kind="recipients"
          title={t('account.recipients')}
          hint={t('account.recipients_hint')}
          multiline
          placeholder={'Destinataire\nService\nAdresse\n75000 Ville'}
        />
        <TextSection
          kind="cities"
          title={t('account.cities')}
          hint={t('account.cities_hint')}
          placeholder={t('account.city_ph')}
        />
        <TextSection
          kind="attachments"
          title={t('account.attachments')}
          hint={t('account.attachments_hint')}
          placeholder={t('account.attachment_ph')}
        />
        <SignatureSection />
      </div>
    </div>
  );
}
